import React, { Suspense } from 'react';
import { Routes, Route, useParams } from 'react-router-dom';
import { RootRedirect } from '../components/routes/RootRedirect';
import { ProtectedRoute } from '../components/auth/ProtectedRoute';
import { LoadingScreen } from '../components/shared/LoadingScreen';
import { GlobalSplashScreen } from '../components/shared/GlobalSplashScreen';
import { PrivacyPolicyPage } from './PrivacyPolicyPage';
import { TermsAndConditionsPage } from './TermsAndConditionsPage';
import { useKeyboardFocus } from '../hooks/useKeyboardFocus';

// Lazy load heavy components for code splitting
const Dashboard = React.lazy(() => import('../components/Dashboard').then(m => ({ default: m.Dashboard })));
const OnboardingWizard = React.lazy(() => import('../components/onboarding/OnboardingWizard').then(m => ({ default: m.OnboardingWizard })));
const PasswordReset = React.lazy(() => import('../components/auth/PasswordReset').then(m => ({ default: m.PasswordReset })));
const PublicSlugRoute = React.lazy(() => import('../components/routes/PublicSlugRoute').then(m => ({ default: m.PublicSlugRoute })));

const InvitePage = React.lazy(() => import('../components/auth/InvitePage').then(m => ({ default: m.InvitePage })));
const OwnershipTransferPage = React.lazy(() => import('../components/auth/OwnershipTransferPage').then(m => ({ default: m.OwnershipTransferPage })));
const ReservationStatusRoute = React.lazy(() => import('./ReservationStatusRoute').then(m => ({ default: m.ReservationStatusRoute })));
const BookingDeeplinkRoute = React.lazy(() => import('./BookingDeeplinkRoute').then(m => ({ default: m.BookingDeeplinkRoute })));
const LoginPage = React.lazy(() => import('../components/auth/LoginPage').then(m => ({ default: m.LoginPage })));
const ResetPasswordRequest = React.lazy(() => import('../components/auth/ResetPasswordRequest').then(m => ({ default: m.ResetPasswordRequest })));
const AuthCallback = React.lazy(() => import('../components/auth/AuthCallback').then(m => ({ default: m.AuthCallback })));
const WelcomePage = React.lazy(() => import('../components/welcome/WelcomePage').then(m => ({ default: m.WelcomePage })));
const IntroStories = React.lazy(() => import('../components/welcome/IntroStories').then(m => ({ default: m.IntroStories })));

// Admin routes removed - now in separate admin app at packages/admin
const AdminImpersonatePage = React.lazy(() => import('../components/shared/AdminImpersonatePage').then(m => ({ default: m.AdminImpersonatePage })));


export function AppRoutes() {
    // Global hook for optimal mobile keyboard UX
    useKeyboardFocus();

    return (
        <div>
            <GlobalSplashScreen />
            <Suspense fallback={<LoadingScreen />}>
                <Routes>

                    {/* Root redirect */}
                    <Route path="/" element={<RootRedirect />} />

                    {/* Auth routes */}
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/reset-password" element={<ResetPasswordRequest />} />

                    {/* Welcome page - first-time experience */}
                    <Route path="/welcome" element={<WelcomePage />} />

                    {/* Intro Stories - New Flow */}
                    <Route path="/intro" element={<IntroStories />} />

                    {/* Onboarding (public/auth) */}
                    <Route path="/onboarding" element={<OnboardingWizard />} />

                    {/* Dashboard (protected) */}
                    <Route
                        path="/dashboard/*"
                        element={
                            <ProtectedRoute>
                                <Dashboard />
                            </ProtectedRoute>
                        }
                    />

                    {/* Admin impersonation endpoint (cross-domain from admin.deskbay.io) */}
                    <Route path="/admin-impersonate" element={<AdminImpersonatePage />} />

                    {/* Public routes */}

                    <Route path="/invite/:token" element={<InvitePage />} />
                    <Route path="/transfer-ownership/:token" element={<OwnershipTransferPage />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route path="/auth/reset-password" element={<PasswordReset />} />
                    <Route path="/res/:code" element={<ReservationStatusRoute />} />
                    {/* Legacy link (pre-2026-07-12), solo visualizzazione — vedi nota in ReservationStatusRoute.tsx */}
                    <Route path="/reservation/:id" element={<ReservationStatusRoute />} />
                    <Route path="/b/:bookingNumber" element={<BookingDeeplinkRoute />} />
                    <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

                    {/* Public restaurant profile (catch-all) */}
                    <Route path="/:slug" element={<PublicSlugRoute />} />
                </Routes>
            </Suspense>
        </div>
    );
}
